import { createAsyncThunk } from '@reduxjs/toolkit';
import apiInstance from '@api/index';
import toastMessage from '@/components/Toast';
import { getThunkError } from '@store/utility/thunkErrorUtils';

// ─── JE Type ──────────────────────────────────────────────────────────────────
export const fetchJeTypes = createAsyncThunk(
  'haAdmin/fetchJeTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.haAdmin.listJeTypes();
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to load JE Types: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const createJeType = createAsyncThunk(
  'haAdmin/createJeType',
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.createJeType(data);
      const response = await apiInstance.haAdmin.listJeTypes();
      toastMessage.success('JE Type created successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to create JE Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const updateJeType = createAsyncThunk(
  'haAdmin/updateJeType',
  async ({ id, data }: { id: number; data: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.updateJeType(id, data);
      const response = await apiInstance.haAdmin.listJeTypes();
      toastMessage.success('JE Type updated successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to update JE Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const deleteJeType = createAsyncThunk(
  'haAdmin/deleteJeType',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.deleteJeType(id);
      const response = await apiInstance.haAdmin.listJeTypes();
      toastMessage.success('JE Type deleted successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to delete JE Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

// ─── JE Overall Type ──────────────────────────────────────────────────────────
export const fetchJeOverallTypes = createAsyncThunk(
  'haAdmin/fetchJeOverallTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.haAdmin.listJeOverallTypes();
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to load JE Overall Types: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const createJeOverallType = createAsyncThunk(
  'haAdmin/createJeOverallType',
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.createJeOverallType(data);
      const response = await apiInstance.haAdmin.listJeOverallTypes();
      toastMessage.success('JE Overall Type created successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to create JE Overall Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const updateJeOverallType = createAsyncThunk(
  'haAdmin/updateJeOverallType',
  async ({ id, data }: { id: number; data: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.updateJeOverallType(id, data);
      const response = await apiInstance.haAdmin.listJeOverallTypes();
      toastMessage.success('JE Overall Type updated successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to update JE Overall Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const deleteJeOverallType = createAsyncThunk(
  'haAdmin/deleteJeOverallType',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.deleteJeOverallType(id);
      const response = await apiInstance.haAdmin.listJeOverallTypes();
      toastMessage.success('JE Overall Type deleted successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to delete JE Overall Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

// ─── Effectiveness Type ───────────────────────────────────────────────────────
export const fetchEffectivenessTypes = createAsyncThunk(
  'haAdmin/fetchEffectivenessTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.haAdmin.listEffectivenessTypes();
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to load Effectiveness Types: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const createEffectivenessType = createAsyncThunk(
  'haAdmin/createEffectivenessType',
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.createEffectivenessType(data);
      const response = await apiInstance.haAdmin.listEffectivenessTypes();
      toastMessage.success('Effectiveness Type created successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to create Effectiveness Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const updateEffectivenessType = createAsyncThunk(
  'haAdmin/updateEffectivenessType',
  async ({ id, data }: { id: number; data: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.updateEffectivenessType(id, data);
      const response = await apiInstance.haAdmin.listEffectivenessTypes();
      toastMessage.success('Effectiveness Type updated successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to update Effectiveness Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const deleteEffectivenessType = createAsyncThunk(
  'haAdmin/deleteEffectivenessType',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.deleteEffectivenessType(id);
      const response = await apiInstance.haAdmin.listEffectivenessTypes();
      toastMessage.success('Effectiveness Type deleted successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to delete Effectiveness Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

// ─── JE Status ────────────────────────────────────────────────────────────────
export const fetchJeStatuses = createAsyncThunk(
  'haAdmin/fetchJeStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.haAdmin.listJeStatuses();
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to load JE Statuses: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const createJeStatus = createAsyncThunk(
  'haAdmin/createJeStatus',
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.createJeStatus(data);
      const response = await apiInstance.haAdmin.listJeStatuses();
      toastMessage.success('JE Status created successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to create JE Status: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const updateJeStatus = createAsyncThunk(
  'haAdmin/updateJeStatus',
  async ({ id, data }: { id: number; data: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.updateJeStatus(id, data);
      const response = await apiInstance.haAdmin.listJeStatuses();
      toastMessage.success('JE Status updated successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to update JE Status: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const deleteJeStatus = createAsyncThunk(
  'haAdmin/deleteJeStatus',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.deleteJeStatus(id);
      const response = await apiInstance.haAdmin.listJeStatuses();
      toastMessage.success('JE Status deleted successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to delete JE Status: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

// ─── Standards Type ───────────────────────────────────────────────────────────
export const fetchStandardsTypes = createAsyncThunk(
  'haAdmin/fetchStandardsTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.haAdmin.listStandardsTypes();
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to load Standards Types: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const createStandardsType = createAsyncThunk(
  'haAdmin/createStandardsType',
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.createStandardsType(data);
      const response = await apiInstance.haAdmin.listStandardsTypes();
      toastMessage.success('Standards Type created successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to create Standards Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const updateStandardsType = createAsyncThunk(
  'haAdmin/updateStandardsType',
  async ({ id, data }: { id: number; data: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.updateStandardsType(id, data);
      const response = await apiInstance.haAdmin.listStandardsTypes();
      toastMessage.success('Standards Type updated successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to update Standards Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const deleteStandardsType = createAsyncThunk(
  'haAdmin/deleteStandardsType',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.deleteStandardsType(id);
      const response = await apiInstance.haAdmin.listStandardsTypes();
      toastMessage.success('Standards Type deleted successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to delete Standards Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

// ─── Designation Method ───────────────────────────────────────────────────────
export const fetchDesignationMethods = createAsyncThunk(
  'haAdmin/fetchDesignationMethods',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.haAdmin.listDesignationMethods();
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to load Designation Methods: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const createDesignationMethod = createAsyncThunk(
  'haAdmin/createDesignationMethod',
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.createDesignationMethod(data);
      const response = await apiInstance.haAdmin.listDesignationMethods();
      toastMessage.success('Designation Method created successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to create Designation Method: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const updateDesignationMethod = createAsyncThunk(
  'haAdmin/updateDesignationMethod',
  async ({ id, data }: { id: number; data: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.updateDesignationMethod(id, data);
      const response = await apiInstance.haAdmin.listDesignationMethods();
      toastMessage.success('Designation Method updated successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to update Designation Method: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const deleteDesignationMethod = createAsyncThunk(
  'haAdmin/deleteDesignationMethod',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.deleteDesignationMethod(id);
      const response = await apiInstance.haAdmin.listDesignationMethods();
      toastMessage.success('Designation Method deleted successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to delete Designation Method: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

// ─── Transaction Amort Type ───────────────────────────────────────────────────
export const fetchTransactionAmortTypes = createAsyncThunk(
  'haAdmin/fetchTransactionAmortTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.haAdmin.listTransactionAmortTypes();
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to load Transaction Amort Types: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const createTransactionAmortType = createAsyncThunk(
  'haAdmin/createTransactionAmortType',
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.createTransactionAmortType(data);
      const response = await apiInstance.haAdmin.listTransactionAmortTypes();
      toastMessage.success('Transaction Amort Type created successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to create Transaction Amort Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const updateTransactionAmortType = createAsyncThunk(
  'haAdmin/updateTransactionAmortType',
  async ({ id, data }: { id: number; data: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.updateTransactionAmortType(id, data);
      const response = await apiInstance.haAdmin.listTransactionAmortTypes();
      toastMessage.success('Transaction Amort Type updated successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to update Transaction Amort Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const deleteTransactionAmortType = createAsyncThunk(
  'haAdmin/deleteTransactionAmortType',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.deleteTransactionAmortType(id);
      const response = await apiInstance.haAdmin.listTransactionAmortTypes();
      toastMessage.success('Transaction Amort Type deleted successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to delete Transaction Amort Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

// ─── JE Standard Template ─────────────────────────────────────────────────────
export const fetchJeStandardTemplates = createAsyncThunk(
  'haAdmin/fetchJeStandardTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.haAdmin.listJeStandardTemplates();
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to load JE Standard Templates: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const createJeStandardTemplate = createAsyncThunk(
  'haAdmin/createJeStandardTemplate',
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.createJeStandardTemplate(data);
      const response = await apiInstance.haAdmin.listJeStandardTemplates();
      toastMessage.success('JE Standard Template created successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to create JE Standard Template: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const updateJeStandardTemplate = createAsyncThunk(
  'haAdmin/updateJeStandardTemplate',
  async ({ id, data }: { id: number; data: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.updateJeStandardTemplate(id, data);
      const response = await apiInstance.haAdmin.listJeStandardTemplates();
      toastMessage.success('JE Standard Template updated successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to update JE Standard Template: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const deleteJeStandardTemplate = createAsyncThunk(
  'haAdmin/deleteJeStandardTemplate',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.deleteJeStandardTemplate(id);
      const response = await apiInstance.haAdmin.listJeStandardTemplates();
      toastMessage.success('JE Standard Template deleted successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to delete JE Standard Template: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

// ─── JE Standard ──────────────────────────────────────────────────────────────
export const fetchJeStandards = createAsyncThunk(
  'haAdmin/fetchJeStandards',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.haAdmin.listJeStandards();
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to load JE Standards: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const createJeStandard = createAsyncThunk(
  'haAdmin/createJeStandard',
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.createJeStandard(data);
      const response = await apiInstance.haAdmin.listJeStandards();
      toastMessage.success('JE Standard created successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to create JE Standard: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const updateJeStandard = createAsyncThunk(
  'haAdmin/updateJeStandard',
  async ({ id, data }: { id: number; data: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.updateJeStandard(id, data);
      const response = await apiInstance.haAdmin.listJeStandards();
      toastMessage.success('JE Standard updated successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to update JE Standard: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const deleteJeStandard = createAsyncThunk(
  'haAdmin/deleteJeStandard',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.deleteJeStandard(id);
      const response = await apiInstance.haAdmin.listJeStandards();
      toastMessage.success('JE Standard deleted successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to delete JE Standard: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

// ─── Transaction Regression Values ───────────────────────────────────────────
export const fetchTransactionRegressionValues = createAsyncThunk(
  'haAdmin/fetchTransactionRegressionValues',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.haAdmin.listTransactionRegressionValues();
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to load Transaction Regression Values: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const createTransactionRegressionValue = createAsyncThunk(
  'haAdmin/createTransactionRegressionValue',
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.createTransactionRegressionValue(data);
      const response = await apiInstance.haAdmin.listTransactionRegressionValues();
      toastMessage.success('Transaction Regression Value created successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to create Transaction Regression Value: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const updateTransactionRegressionValue = createAsyncThunk(
  'haAdmin/updateTransactionRegressionValue',
  async ({ id, data }: { id: number; data: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.updateTransactionRegressionValue(id, data);
      const response = await apiInstance.haAdmin.listTransactionRegressionValues();
      toastMessage.success('Transaction Regression Value updated successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to update Transaction Regression Value: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const deleteTransactionRegressionValue = createAsyncThunk(
  'haAdmin/deleteTransactionRegressionValue',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.deleteTransactionRegressionValue(id);
      const response = await apiInstance.haAdmin.listTransactionRegressionValues();
      toastMessage.success('Transaction Regression Value deleted successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to delete Transaction Regression Value: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

// ─── Property Movement Type ───────────────────────────────────────────────────
export const fetchPropertyMovementTypes = createAsyncThunk(
  'haAdmin/fetchPropertyMovementTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.haAdmin.listPropertyMovementTypes();
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to load Property Movement Types: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const createPropertyMovementType = createAsyncThunk(
  'haAdmin/createPropertyMovementType',
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.createPropertyMovementType(data);
      const response = await apiInstance.haAdmin.listPropertyMovementTypes();
      toastMessage.success('Property Movement Type created successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to create Property Movement Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const updatePropertyMovementType = createAsyncThunk(
  'haAdmin/updatePropertyMovementType',
  async ({ id, data }: { id: number; data: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.updatePropertyMovementType(id, data);
      const response = await apiInstance.haAdmin.listPropertyMovementTypes();
      toastMessage.success('Property Movement Type updated successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to update Property Movement Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);

export const deletePropertyMovementType = createAsyncThunk(
  'haAdmin/deletePropertyMovementType',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiInstance.haAdmin.deletePropertyMovementType(id);
      const response = await apiInstance.haAdmin.listPropertyMovementTypes();
      toastMessage.success('Property Movement Type deleted successfully.');
      return response.data;
    } catch (error) {
      toastMessage.error(`Failed to delete Property Movement Type: ${getThunkError(error)}`);
      return rejectWithValue(error);
    }
  },
);
